import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiClient, axiosClient } from '../../auth-lib';
import OrgAdminLayout from '../components/OrgAdminLayout';
import ClientAccessManager from '../components/ClientAccessManager';
import { Button, Card, StatusPill, Table, Skeleton, EmptyState, useToast } from '../../design-system';
import '../styles/UserProfile.css';

const TASK_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'open', label: 'Open' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'completed', label: 'Completed' },
];

const initialsOf = (name = '') =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('');

const monthKey = (d) => {
  const dt = new Date(d);
  return `${dt.toLocaleString('en-IN', { month: 'long' })} ${dt.getFullYear()}`;
};

function OrgAdminUserProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [person, setPerson] = useState(null); // identity from auth-service
  const [profile, setProfile] = useState(null); // workload aggregate from hrms
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [taskFilter, setTaskFilter] = useState('all');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setDenied(false);
      try {
        const [userRes, profileRes] = await Promise.all([
          axiosClient.get(`/users/${id}`),
          apiClient.get(`/api/reports/employee-profile?userId=${id}`),
        ]);
        if (cancelled) return;
        setPerson(userRes.data);
        setProfile(profileRes.data);
      } catch (err) {
        if (cancelled) return;
        if (err.response?.status === 403) {
          setDenied(true);
        } else {
          toast.danger(err.response?.data?.error || 'Failed to load employee profile');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const stats = profile?.stats;

  const visibleTasks = useMemo(() => {
    const tasks = profile?.tasks || [];
    if (taskFilter === 'open') return tasks.filter((t) => !(t.status === 'completed' || t.workflowStatus === 'done'));
    if (taskFilter === 'overdue') return tasks.filter((t) => t.overdue);
    if (taskFilter === 'completed') return tasks.filter((t) => t.status === 'completed' || t.workflowStatus === 'done');
    return tasks;
  }, [profile, taskFilter]);

  // Calendar rows grouped into months, in due-date order.
  const calendarByMonth = useMemo(() => {
    const groups = [];
    for (const item of profile?.calendar || []) {
      const key = monthKey(item.dueDate);
      const last = groups[groups.length - 1];
      if (last && last.month === key) last.items.push(item);
      else groups.push({ month: key, items: [item] });
    }
    return groups;
  }, [profile]);

  if (denied) {
    return (
      <OrgAdminLayout title="Employee Profile">
        <EmptyState
          icon="🔒"
          title="Managers and above only"
          description="This page exposes an individual's workload and performance, so it needs Manager level or higher."
        />
      </OrgAdminLayout>
    );
  }

  return (
    <OrgAdminLayout
      title={person?.name || 'Employee Profile'}
      subtitle="Everything linked to this employee"
      headerRight={
        <Button variant="secondary" size="sm" onClick={() => navigate('/org-admin/users')}>
          ← All Users
        </Button>
      }
    >
      <div className="user-profile-page">
        {/* Identity */}
        <Card className="profile-header-card">
          {loading ? (
            <Skeleton height={72} />
          ) : (
            <div className="profile-header">
              <div className="profile-avatar">{initialsOf(person?.name)}</div>
              <div className="profile-identity">
                <div className="profile-name-row">
                  <h2>{person?.name}</h2>
                  {person?.role?.name && <StatusPill variant="neutral" label={person.role.name} />}
                  <StatusPill status={person?.isActive ? 'active' : 'inactive'} />
                </div>
                <div className="profile-meta">
                  <span>{person?.email}</span>
                  {person?.phone && <span>{person.phone}</span>}
                  {person?.createdAt && <span>Joined {new Date(person.createdAt).toLocaleDateString()}</span>}
                  {person?.lastLogin && <span>Last login {new Date(person.lastLogin).toLocaleString()}</span>}
                </div>
              </div>
            </div>
          )}
        </Card>

        {/* Workload stats */}
        <div className="profile-stats-grid">
          {[
            { label: 'Total Tasks', value: stats?.total },
            { label: 'Completed', value: stats?.completed },
            { label: 'In Progress', value: stats?.inProgress },
            { label: 'Overdue', value: stats?.overdue, danger: true },
            { label: 'Completion Rate', value: stats != null ? `${stats.completionRate}%` : null },
            {
              label: 'Hours (logged / budgeted)',
              value: stats != null ? `${stats.loggedHours} / ${stats.allocatedHours}` : null,
              danger: stats != null && stats.allocatedHours > 0 && stats.loggedHours > stats.allocatedHours,
            },
          ].map((s) => (
            <Card key={s.label} className={`profile-stat-card${s.danger && s.value ? ' danger' : ''}`}>
              {loading ? <Skeleton height={30} width="50%" /> : <div className="profile-stat-number">{s.value ?? 0}</div>}
              <div className="profile-stat-label">{s.label}</div>
            </Card>
          ))}
        </div>

        {/* Performance */}
        <Card className="profile-section">
          <Card.Header title="Performance" subtitle="Completion and involvement across their tasks" />
          {loading ? (
            <Skeleton height={80} />
          ) : (
            <div className="profile-performance">
              <div className="perf-bar-block">
                <div className="perf-bar-label">
                  <span>Completion</span>
                  <span>{stats?.completionRate ?? 0}%</span>
                </div>
                <div className="perf-bar">
                  <div className="perf-bar-fill" style={{ width: `${stats?.completionRate ?? 0}%` }} />
                </div>
              </div>
              {stats?.allocatedHours > 0 && (
                <div className="perf-bar-block">
                  <div className="perf-bar-label">
                    <span>Budget utilisation</span>
                    <span>
                      {Math.round((stats.loggedHours / stats.allocatedHours) * 100)}%
                    </span>
                  </div>
                  <div className="perf-bar">
                    <div
                      className={`perf-bar-fill${stats.loggedHours > stats.allocatedHours ? ' over' : ''}`}
                      style={{ width: `${Math.min(100, Math.round((stats.loggedHours / stats.allocatedHours) * 100))}%` }}
                    />
                  </div>
                </div>
              )}
              <div className="perf-involvement">
                {Object.entries({
                  Preparer: stats?.byInvolvement?.preparer,
                  Reviewer: stats?.byInvolvement?.reviewer,
                  Assistant: stats?.byInvolvement?.assistant,
                  Assigned: stats?.byInvolvement?.assigned,
                  Creator: stats?.byInvolvement?.creator,
                }).map(([label, count]) =>
                  count ? <StatusPill key={label} variant="neutral" label={`${label}: ${count}`} /> : null
                )}
              </div>
            </div>
          )}
        </Card>

        {/* Clients */}
        <Card className="profile-section">
          <Card.Header title="Clients" subtitle="Where this employee is partner, manager, preparer or reviewer" />
          {!loading && (profile?.clients?.length ?? 0) === 0 ? (
            <EmptyState icon="🏢" title="No clients linked" description="Assignments on clients and enrollment defaults appear here." />
          ) : (
            <Table
              columns={[
                { key: 'name', header: 'Client', render: (c) => c.name },
                {
                  key: 'relationships',
                  header: 'Relationship',
                  render: (c) => (
                    <div className="profile-rel-pills">
                      {c.relationships.map((r, i) => (
                        <StatusPill key={i} variant="neutral" label={r} />
                      ))}
                    </div>
                  ),
                },
                { key: 'status', header: 'Status', render: (c) => <StatusPill status={c.status} /> },
              ]}
              data={profile?.clients || []}
              loading={loading}
              onRowClick={(c) => navigate(`/org-admin/clients/${c.id}`)}
            />
          )}
        </Card>

        {/* Client access (admin only — renders nothing for lower roles) */}
        <ClientAccessManager userId={id} />

        {/* Tasks */}
        <Card className="profile-section">
          <Card.Header title="Tasks" subtitle="Every task where they appear in any capacity" />
          <div className="profile-task-filters">
            {TASK_FILTERS.map((f) => (
              <Button
                key={f.value}
                variant={taskFilter === f.value ? 'primary' : 'secondary'}
                size="sm"
                onClick={() => setTaskFilter(f.value)}
              >
                {f.label}
              </Button>
            ))}
          </div>
          {!loading && visibleTasks.length === 0 ? (
            <EmptyState icon="📭" title="No tasks" description="Nothing matches this filter." />
          ) : (
            <Table
              columns={[
                {
                  key: 'taskNumber',
                  header: '#',
                  render: (t) => (t.taskNumber != null ? `TASK-${String(t.taskNumber).padStart(4, '0')}` : '-'),
                },
                { key: 'title', header: 'Task', render: (t) => t.title },
                { key: 'client', header: 'Client', render: (t) => t.client || '—' },
                {
                  key: 'roles',
                  header: 'Their Role',
                  render: (t) => t.roles.join(', ') || '—',
                },
                {
                  key: 'status',
                  header: 'Status',
                  render: (t) => <StatusPill status={t.status} label={t.status === 'todo' ? 'Pending' : undefined} />,
                },
                {
                  key: 'dueDate',
                  header: 'Due',
                  render: (t) =>
                    t.dueDate ? (
                      <span className={t.overdue ? 'profile-overdue' : undefined}>
                        {new Date(t.dueDate).toLocaleDateString()}
                      </span>
                    ) : (
                      '—'
                    ),
                },
              ]}
              data={visibleTasks}
              loading={loading}
              onRowClick={(t) => navigate(`/org-admin/tasks/${t.id}`)}
              keyField="id"
            />
          )}
        </Card>

        {/* Personal compliance calendar */}
        <Card className="profile-section">
          <Card.Header
            title="Compliance Calendar"
            subtitle="Statutory deadlines flowing from their enrollments and tasks — overdue items stay until resolved"
          />
          {loading ? (
            <Skeleton height={100} />
          ) : calendarByMonth.length === 0 ? (
            <EmptyState
              icon="🗓️"
              title="No upcoming compliance deadlines"
              description="Deadlines appear once compliance instances involving this employee have due dates."
            />
          ) : (
            <div className="profile-calendar">
              {calendarByMonth.map((group) => (
                <div key={group.month} className="calendar-month">
                  <div className="calendar-month-title">{group.month}</div>
                  {group.items.map((item) => (
                    <div
                      key={item.id}
                      className={`calendar-item${item.overdue ? ' overdue' : ''}${item.taskId ? ' clickable' : ''}`}
                      onClick={item.taskId ? () => navigate(`/org-admin/tasks/${item.taskId}`) : undefined}
                    >
                      <span className="calendar-date">
                        {new Date(item.dueDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                      </span>
                      <span className="calendar-what">
                        <strong>{item.compliance}</strong> — {item.client}
                        <span className="calendar-period"> · {item.period}</span>
                      </span>
                      <StatusPill status={item.overdue ? 'overdue' : item.status} />
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </OrgAdminLayout>
  );
}

export default OrgAdminUserProfile;
